import {
    Alert,
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Grid,
    IconButton,
    MenuItem,
    Paper,
    Select,
    Tab,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tabs,
    TextField,
    Typography
} from '@mui/material';
import ChevronLeftIcon from '@mui/icons-material/ChevronLeft';
import ChevronRightIcon from '@mui/icons-material/ChevronRight';
import { useEffect, useMemo, useState } from 'react';
import { API_URL } from '../config/api';

const formatMoney = (value) => `${Number(value || 0).toLocaleString('ru-RU')} ₽`;
const formatNum = (value) => Number(value || 0).toLocaleString('ru-RU');

const getMonthRange = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const date_from = `${year}-${String(month + 1).padStart(2, '0')}-01`;
    const date_to = `${year}-${String(month + 1).padStart(2, '0')}-${String(new Date(year, month + 1, 0).getDate()).padStart(2, '0')}`;
    return { date_from, date_to };
};

const getPeriodDates = (period, monthDate = new Date()) => {
    const now = new Date();
    const today = now.toISOString().split('T')[0];

    if (period === 'day') {
        return { date_from: today, date_to: today };
    }
    if (period === 'week') {
        const from = new Date(now);
        from.setDate(from.getDate() - 6);
        return { date_from: from.toISOString().split('T')[0], date_to: today };
    }
    if (period === 'month') {
        return getMonthRange(monthDate);
    }
    return null;
};

function MoneyCell({ value, highlight }) {
    const n = Number(value || 0);
    return (
        <TableCell
            align="right"
            sx={{
                fontVariantNumeric: 'tabular-nums',
                bgcolor: highlight ? 'rgba(255, 243, 224, 0.6)' : undefined,
                fontWeight: n > 0 ? 600 : 400,
                color: n > 0 ? 'text.primary' : 'text.disabled'
            }}
        >
            {n > 0 ? formatNum(n) : '—'}
        </TableCell>
    );
}

function StatisticsPanel({ token }) {
    const [period, setPeriod] = useState('month');
    const [currentMonth, setCurrentMonth] = useState(() => new Date());
    const [dateRange, setDateRange] = useState(getPeriodDates('month'));
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [data, setData] = useState(null);
    const [tab, setTab] = useState(0);

    const loadData = async (range = dateRange) => {
        if (!range?.date_from || !range?.date_to) return;
        setLoading(true);
        setError('');
        try {
            const params = new URLSearchParams(range);
            const response = await fetch(`${API_URL}/api/statistics/revenue-table?${params.toString()}`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            const result = await response.json();
            if (result.success) {
                setData(result);
            } else {
                setError(result.error || 'Не удалось загрузить статистику');
            }
        } catch (err) {
            console.error('Error loading statistics:', err);
            setError('Ошибка сети при загрузке статистики');
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        const next = getPeriodDates(period, currentMonth);
        if (next) {
            setDateRange(next);
            loadData(next);
        }
    }, [period, currentMonth]);

    const days = data?.days || [];
    const totals = data?.totals || {};
    const sources = data?.sources || [];
    const popularCafe = data?.popular_cafe || [];
    const popularSouvenirs = data?.popular_souvenirs || [];
    const commissions = data?.commissions || {};

    const kpi = useMemo(() => ([
        { label: 'Билетов', value: formatNum(totals.tickets), sub: 'продано за период' },
        { label: 'Прошли по факту', value: formatNum(totals.attended), sub: 'статус «Были»' },
        { label: 'Общая НАЛ', value: formatMoney(totals.total_cash), sub: 'наличные' },
        { label: 'Общая БЕЗНАЛ', value: formatMoney(totals.total_noncash), sub: 'карта / СБП / онлайн' },
        { label: 'Яндекс билеты', value: formatMoney(totals.total_yandex), sub: 'оплата через Яндекс.Билеты' },
        { label: 'По счету', value: formatMoney(totals.total_invoice), sub: 'оплата по счету' },
        { label: 'Экскурсия', value: formatMoney((totals.excursion_cash || 0) + (totals.excursion_noncash || 0) + (totals.excursion_yandex || 0) + (totals.excursion_invoice || 0)), sub: `НАЛ ${formatNum(totals.excursion_cash)} / БН ${formatNum(totals.excursion_noncash)} / ЯН ${formatNum(totals.excursion_yandex)} / СЧЕТ ${formatNum(totals.excursion_invoice)}` },
        { label: 'Бар (в кафе)', value: formatMoney((totals.bar_cafe_cash || 0) + (totals.bar_cafe_noncash || 0) + (totals.bar_cafe_invoice || 0)), sub: 'комиссия гидам 10%' },
        { label: 'Бар (до/после)', value: formatMoney((totals.bar_before_cash || 0) + (totals.bar_before_noncash || 0) + (totals.bar_before_invoice || 0)), sub: 'не входит в 10% гидам' },
        { label: 'Сувениры', value: formatMoney((totals.souvenirs_cash || 0) + (totals.souvenirs_noncash || 0) + (totals.souvenirs_invoice || 0)), sub: 'комиссия админам 7%' }
    ]), [totals]);

    return (
        <Box sx={{ p: 3 }}>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', gap: 2, flexWrap: 'wrap', mb: 2 }}>
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>Статистика выручки</Typography>
                    <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                        Экскурсия, бар, сувениры. Яндекс и «По счету» отдельно от безнала (карта / СБП / онлайн)
                    </Typography>
                </Box>
                <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
                    <Select size="small" value={period} onChange={(e) => setPeriod(e.target.value)}>
                        <MenuItem value="day">Сегодня</MenuItem>
                        <MenuItem value="week">7 дней</MenuItem>
                        <MenuItem value="month">Месяц</MenuItem>
                        <MenuItem value="custom">Период</MenuItem>
                    </Select>
                    {period === 'month' && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <IconButton size="small" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() - 1, 1))}>
                                <ChevronLeftIcon />
                            </IconButton>
                            <Typography variant="body2" sx={{ minWidth: 140, textAlign: 'center', textTransform: 'capitalize' }}>
                                {currentMonth.toLocaleDateString('ru-RU', { month: 'long', year: 'numeric' })}
                            </Typography>
                            <IconButton size="small" onClick={() => setCurrentMonth(new Date(currentMonth.getFullYear(), currentMonth.getMonth() + 1, 1))}>
                                <ChevronRightIcon />
                            </IconButton>
                        </Box>
                    )}
                    <TextField
                        size="small"
                        type="date"
                        label="С"
                        value={dateRange.date_from}
                        onChange={(e) => {
                            setPeriod('custom');
                            setDateRange((prev) => ({ ...prev, date_from: e.target.value }));
                        }}
                        InputLabelProps={{ shrink: true }}
                    />
                    <TextField
                        size="small"
                        type="date"
                        label="По"
                        value={dateRange.date_to}
                        onChange={(e) => {
                            setPeriod('custom');
                            setDateRange((prev) => ({ ...prev, date_to: e.target.value }));
                        }}
                        onBlur={() => loadData()}
                        InputLabelProps={{ shrink: true }}
                    />
                </Box>
            </Box>

            {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

            {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', py: 8 }}>
                    <CircularProgress />
                </Box>
            ) : (
                <>
                    <Grid container spacing={2} sx={{ mb: 3 }}>
                        {kpi.map((item) => (
                            <Grid item xs={12} sm={6} md={4} lg={2} key={item.label}>
                                <Card variant="outlined" sx={{ height: '100%' }}>
                                    <CardContent sx={{ py: 1.5, '&:last-child': { pb: 1.5 } }}>
                                        <Typography variant="caption" color="text.secondary">{item.label}</Typography>
                                        <Typography variant="h6" sx={{ fontWeight: 700, mt: 0.5, fontSize: '1.05rem' }}>
                                            {item.value}
                                        </Typography>
                                        <Typography variant="caption" color="text.secondary">{item.sub}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>

                    {(commissions.guide_commission_total > 0 || commissions.admin_commission_total > 0) && (
                        <Alert severity="info" sx={{ mb: 2 }}>
                            Комиссии за период: гидам 10% от бара в кафе — <strong>{formatMoney(commissions.guide_commission_total)}</strong>
                            {' · '}
                            админам 7% от сувениров — <strong>{formatMoney(commissions.admin_commission_total)}</strong>
                        </Alert>
                    )}

                    <Tabs value={tab} onChange={(_, v) => setTab(v)} sx={{ mb: 2 }}>
                        <Tab label="Таблица по дням" />
                        <Tab label="Популярность" />
                        <Tab label="Откуда узнали" />
                    </Tabs>

                    {tab === 0 && (
                        <TableContainer component={Paper} sx={{ maxHeight: '70vh', overflow: 'auto' }}>
                            <Table size="small" stickyHeader>
                                <TableHead>
                                    <TableRow>
                                        <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Дата</TableCell>
                                        <TableCell rowSpan={2} sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>День</TableCell>
                                        <TableCell colSpan={4} align="center" sx={{ fontWeight: 700, bgcolor: '#fff8e1' }}>Общая выручка</TableCell>
                                        <TableCell colSpan={4} align="center" sx={{ fontWeight: 700, bgcolor: '#fff59d' }}>Экскурсия</TableCell>
                                        <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#bbdefb' }}>Бар (в кафе)</TableCell>
                                        <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#b3e5fc' }}>Бар (до/после)</TableCell>
                                        <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#c8e6c9' }}>Сертификаты</TableCell>
                                        <TableCell colSpan={2} align="center" sx={{ fontWeight: 700, bgcolor: '#ffcdd2' }}>Сувениры</TableCell>
                                        <TableCell rowSpan={2} align="right" sx={{ fontWeight: 700, bgcolor: '#f5f5f5' }}>Билеты</TableCell>
                                        <TableCell rowSpan={2} align="right" sx={{ fontWeight: 700, bgcolor: '#e8f5e9' }}>По факту</TableCell>
                                    </TableRow>
                                    <TableRow>
                                        {['НАЛ', 'БЕЗНАЛ', 'ЯНДЕКС', 'СЧЕТ', 'НАЛ', 'БЕЗНАЛ', 'ЯНДЕКС', 'СЧЕТ', 'НАЛ', 'БЕЗНАЛ', 'НАЛ', 'БЕЗНАЛ', 'НАЛ', 'БЕЗНАЛ', 'НАЛ', 'БЕЗНАЛ'].map((label, i) => (
                                            <TableCell key={`${label}-${i}`} align="right" sx={{ fontWeight: 600, bgcolor: '#fafafa', fontSize: '0.75rem' }}>
                                                {label}
                                            </TableCell>
                                        ))}
                                    </TableRow>
                                </TableHead>
                                <TableBody>
                                    {days.map((day) => (
                                        <TableRow
                                            key={day.date}
                                            hover
                                            sx={{ bgcolor: day.is_weekend ? '#fffde7' : undefined }}
                                        >
                                            <TableCell>
                                                {new Date(`${day.date}T12:00:00`).toLocaleDateString('ru-RU', { day: '2-digit', month: '2-digit' })}
                                            </TableCell>
                                            <TableCell>{day.day_of_week}</TableCell>
                                            <MoneyCell value={day.total_cash} highlight />
                                            <MoneyCell value={day.total_noncash} highlight />
                                            <MoneyCell value={day.total_yandex} highlight />
                                            <MoneyCell value={day.total_invoice} highlight />
                                            <MoneyCell value={day.excursion_cash} />
                                            <MoneyCell value={day.excursion_noncash} />
                                            <MoneyCell value={day.excursion_yandex} />
                                            <MoneyCell value={day.excursion_invoice} />
                                            <MoneyCell value={day.bar_cafe_cash} />
                                            <MoneyCell value={day.bar_cafe_noncash} />
                                            <MoneyCell value={day.bar_before_cash} />
                                            <MoneyCell value={day.bar_before_noncash} />
                                            <MoneyCell value={day.certificates_cash} />
                                            <MoneyCell value={day.certificates_noncash} />
                                            <MoneyCell value={day.souvenirs_cash} />
                                            <MoneyCell value={day.souvenirs_noncash} />
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {day.tickets > 0 ? formatNum(day.tickets) : '—'}
                                            </TableCell>
                                            <TableCell align="right" sx={{ fontWeight: 600 }}>
                                                {day.attended > 0 ? formatNum(day.attended) : '—'}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                                    <TableRow sx={{ bgcolor: '#eceff1' }}>
                                        <TableCell colSpan={2} sx={{ fontWeight: 800 }}>ИТОГО</TableCell>
                                        <MoneyCell value={totals.total_cash} highlight />
                                        <MoneyCell value={totals.total_noncash} highlight />
                                        <MoneyCell value={totals.total_yandex} highlight />
                                        <MoneyCell value={totals.total_invoice} highlight />
                                        <MoneyCell value={totals.excursion_cash} />
                                        <MoneyCell value={totals.excursion_noncash} />
                                        <MoneyCell value={totals.excursion_yandex} />
                                        <MoneyCell value={totals.excursion_invoice} />
                                        <MoneyCell value={totals.bar_cafe_cash} />
                                        <MoneyCell value={totals.bar_cafe_noncash} />
                                        <MoneyCell value={totals.bar_before_cash} />
                                        <MoneyCell value={totals.bar_before_noncash} />
                                        <MoneyCell value={totals.certificates_cash} />
                                        <MoneyCell value={totals.certificates_noncash} />
                                        <MoneyCell value={totals.souvenirs_cash} />
                                        <MoneyCell value={totals.souvenirs_noncash} />
                                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                                            {formatNum(totals.tickets)}
                                        </TableCell>
                                        <TableCell align="right" sx={{ fontWeight: 800 }}>
                                            {formatNum(totals.attended)}
                                        </TableCell>
                                    </TableRow>
                                </TableBody>
                            </Table>
                        </TableContainer>
                    )}

                    {tab === 1 && (
                        <Grid container spacing={3}>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Популярное в кафе</Typography>
                                    {popularCafe.length === 0 ? (
                                        <Typography color="text.secondary">Нет продаж позиций кафе за период</Typography>
                                    ) : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Позиция</TableCell>
                                                    <TableCell>Категория</TableCell>
                                                    <TableCell align="right">Шт</TableCell>
                                                    <TableCell align="right">Выручка</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {popularCafe.map((row, idx) => (
                                                    <TableRow key={`${row.name}-${idx}`}>
                                                        <TableCell>
                                                            <Chip size="small" label={idx + 1} sx={{ mr: 1 }} />
                                                            {row.name}
                                                        </TableCell>
                                                        <TableCell>{row.category || '—'}</TableCell>
                                                        <TableCell align="right">{formatNum(row.qty)}</TableCell>
                                                        <TableCell align="right">{formatMoney(row.revenue)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Paper>
                            </Grid>
                            <Grid item xs={12} md={6}>
                                <Paper sx={{ p: 2 }}>
                                    <Typography variant="h6" sx={{ mb: 2 }}>Популярное в сувенирке</Typography>
                                    {popularSouvenirs.length === 0 ? (
                                        <Typography color="text.secondary">Нет продаж сувениров за период</Typography>
                                    ) : (
                                        <Table size="small">
                                            <TableHead>
                                                <TableRow>
                                                    <TableCell>Позиция</TableCell>
                                                    <TableCell align="right">Шт</TableCell>
                                                    <TableCell align="right">Выручка</TableCell>
                                                </TableRow>
                                            </TableHead>
                                            <TableBody>
                                                {popularSouvenirs.map((row, idx) => (
                                                    <TableRow key={`${row.name}-${idx}`}>
                                                        <TableCell>
                                                            <Chip size="small" label={idx + 1} sx={{ mr: 1 }} />
                                                            {row.name}
                                                        </TableCell>
                                                        <TableCell align="right">{formatNum(row.qty)}</TableCell>
                                                        <TableCell align="right">{formatMoney(row.revenue)}</TableCell>
                                                    </TableRow>
                                                ))}
                                            </TableBody>
                                        </Table>
                                    )}
                                </Paper>
                            </Grid>
                        </Grid>
                    )}

                    {tab === 2 && (
                        <Paper sx={{ p: 2 }}>
                            <Typography variant="h6" sx={{ mb: 1 }}>Откуда узнали (билеты)</Typography>
                            <Alert severity="info" sx={{ mb: 2 }}>
                                Лиды из Яндекс.Билетов сами по себе не считаются «Яндекс Афишей». Пока гостя не спросили, источник — «Не указано».
                                Яндекс Афиша, МТС Live, Кассир, сайт и остальные отмечаются вручную при опросе.
                            </Alert>
                            {sources.length === 0 ? (
                                <Typography color="text.secondary">Нет данных за период</Typography>
                            ) : (
                                <Table size="small">
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>Источник</TableCell>
                                            <TableCell align="right">Билеты</TableCell>
                                            <TableCell align="right">Выручка</TableCell>
                                            <TableCell align="right">Доля</TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {sources.map((source) => {
                                            const share = totals.tickets > 0
                                                ? ((Number(source.tickets) / totals.tickets) * 100).toFixed(1)
                                                : 0;
                                            return (
                                                <TableRow key={source.source_name}>
                                                    <TableCell>{source.source_name}</TableCell>
                                                    <TableCell align="right">{formatNum(source.tickets)}</TableCell>
                                                    <TableCell align="right">{formatMoney(source.revenue)}</TableCell>
                                                    <TableCell align="right">{share}%</TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            )}
                        </Paper>
                    )}
                </>
            )}
        </Box>
    );
}

export default StatisticsPanel;
